import makeStyles from "@mui/styles/makeStyles";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import React, {Fragment, useEffect, useState} from "react";
import _ from "lodash";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import {Button, Grid, Paper, Tooltip} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import moment from "moment";
import config from "../../app/config";
import DialogActions from "@mui/material/DialogActions";
import {hasPermission, Permissions, user} from '../../store/reducers/user';
import MessageBox from "../../util/messageBox";
import clsx from "clsx";
import {updatePatientData} from "../../store/reducers/ward";
import {addNoteForPatient} from "../../store/reducers/notes";
import API from "../../app/api";
import Divider from "@mui/material/Divider";
import Formatter from "../../util/formatter";
import useOpenState from "../../hooks/useOpenState";
import {setSnackbar} from "../../store/reducers/globals";
import {nextWorkflowStep, workflow} from '../../store/reducers/workflow';
import AgyleButton from '../ui/buttons/agyleButton';
import AgyleDialog from '../modal/agyleDialog';
import Notes from '../../util/notes';
import {getTableScrollbar} from '../../util/scrollbar';
import {insertNewAlert} from '../../store/reducers/additionalData';
import {UtilsMomentJS} from '../../store/utilsMoment';
import {WORKFLOW_OPTIONS} from '../../util/workflowUtil';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.default,
    },
    dialogTitle: {
        textAlign: "center",
        padding: "10px"
    },
    historyPanel: {
        minHeight: "400px",
        maxHeight: "400px",
        border: "solid 1px #666",
        padding: "5px",
        overflowY: "scroll",
        backgroundColor: theme.palette.background.paper,
    },
    historyButton: {
        borderRadius: "3px",
        width: "100%",
        minHeight: "60px",
        marginBottom: "5px",
        background: "#666666",
        color: "#fff",
        "&:hover": {
            background: "#999999",
        },
    },
        historyButtonActive: {
        borderRadius: "3px",
        width: "100%",
        minHeight: "60px",
        marginBottom: "5px",
        background: "#999999",
        color: "#fff",
    },
    detailsPanel: {
        minHeight: "400px",
        maxHeight: "400px",
        border: "solid 1px #666",
        padding: "5px",
        overflowY: "scroll",
        backgroundColor: theme.palette.background.paper,
        textAlign: "left",
    },
    canceled: {
        textDecoration: "line-through"
    },
    triage: {
        textAlign: "center",
        fontStyle: "italic",
        textDecoration: "underline"
    }
}));

const MultipleEntriesFormList = (props) => {
    const classesLocal = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // User Info
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // Data states
    const [dataSet, setDataSet] = useState([]);
    const [selected, setSelected] = useState(null);

    // Open form state
    const [openDialog, setOpenDialog] = useState(false);
    const [isConfirmDeleteOpen, openConfirmDelete, closeConfirmDelete] = useOpenState(false, false);
    const [isConfirmRePrintOpen, openConfirmRePrint, closeConfirmRePrint] = useOpenState(false, false);

    const [extended, setExtended] = useState(false);
    const [loadDialog, setLoadDialog] = useState(false);


    const workflowData = useSelector(workflow);

    useEffect(() => {
        const entry = _.get(props.patient, props.field);
        if (!_.isEmpty(props.override)) {
            setDataSet(props.override);
        } else if (!_.isNil(entry) && !_.isNil(entry?.list)) {
            setDataSet(entry.list);
        }
    }, [props.override]);

    const validateObject = (obj) => {
        if (_.isArray(obj) && !_.isEmpty(obj)) {
            return obj[0].hasOwnProperty("value") && obj[0].hasOwnProperty("noteToAddWithRefId")
                && obj[0].hasOwnProperty("referenceId");
        }
        return false;
    };

    const saveData = (obj) => {
        if (!_.isEmpty(obj) && validateObject(obj)) {
            const cloneList = _.cloneDeep(dataSet);
            cloneList.unshift(obj[0].value);
            setDataSet(cloneList);

            let data = [{
                name: props.field,
                value: {
                    list: [...cloneList],
                    by: tester,
                    login: userObject?.userName,
                    date: moment().toISOString()
                },
                noteToAddWithRefId: [{
                    noteToAddWithRefId: obj[0].noteToAddWithRefId,
                    referenceId: obj[0].referenceId
                }],
                idToTrack: obj[0]?.idToTrack || null,
            }];
            if (obj.length > 1) {
                data = data.concat(obj.slice(1));
            }
            props.onSave(data);
        }
    };

    const deleteEntry = () => {
        closeConfirmDelete()
        const cancelObj = {
            ...selected,
            canceled: true,
        };

        let array = _.cloneDeep(dataSet);
        const index = _.findIndex(array, {id: cancelObj.id});
        if (index !== -1) {
            array.splice(index, 1, cancelObj);
        } else {
            array.push(cancelObj);
        }

        setDataSet(array);

        let note = `# ${props.title}\n\n`;
        note += `* Entry at ${moment(selected.date, config.defaultDateTimeFormat).isValid()
            ? selected.date : moment(selected.date).format(config.defaultDateTimeFormat)} has been cancelled`;

        const data = [{
            [props.field]: {
                list: [...array],
                by: tester,
                login: userObject?.userName,
                date: moment().toISOString()
            },
        }];

        const id = encodeURIComponent(cancelObj.id);
        API.cancelClinicalNoteByRefId(props.patient, id)
            .then(() => {
                dispatch(updatePatientData(props.patient, data));
                dispatch(addNoteForPatient(props.patient.patient.__id, {
                    note: note,
                    noteType: props.field,
                    patientsInWardsRef: [props.patient.__id],
                    episodeId: props.patient?.admission?.consultantEpisodeId || null,
                }, props.patient.__id));
                setSelected(null);
                dispatch(setSnackbar({type: 'success', message: 'Form canceled successfully.'}));

                if (props.field === 'studies.rockwood' && config?.clinicalFrailtyScoreAlertAfter6) {
                    const filter = _.filter(array, l => !l.canceled);
                    if (!_.isEmpty(filter) && (Number(filter?.[0]?.scale?.__code) >= 6)){
                        dispatch(insertNewAlert('cfsOver6', props.patient?.__id, props.patient?.patient?.__id, userObject, props.patient?.admission?.consultantEpisodeId));
                    } else {
                        dispatch(insertNewAlert('cancelCfsOver6', props.patient?.__id, props.patient?.patient?.__id, userObject, props.patient?.admission?.consultantEpisodeId));
                    }
                }
            })
            .catch(e => {
                console.error(e);
                dispatch(setSnackbar({type: "error", message: "Form was not successfully canceled."}));
            })

        if (props.modifyPatient) {
            const dataToEdit = [{
                name: props.field,
                value: {
                    list: [...array],
                    by: tester,
                    login: userObject?.userName,
                    date: moment().toISOString()
                }
            }];

            props.modifyPatient(dataToEdit);
        }
    };

    const rePrint = () => {
        closeConfirmRePrint();
        if (selected) {
            let clone = _.cloneDeep(selected);
            let form = _.cloneDeep(_.get(props.patient, props.field));
            const index = _.findIndex(form?.list, {id: clone.id});

            clone.values.lastPrintBy = userObject?.userName;
            clone.values.lastPrintDate = moment().toISOString();
            clone.values.printCount += 1;

            if (index !== -1) {
                form.list[index] = clone;

                let data = [{
                    name: props.field,
                    value: form,
                    noteToAdd: Notes.getRePrintPrescriptionNote(clone),
                    idToTrack: clone.id,
                    rePrint: true,
                }];
                dispatch(setSnackbar({type: 'info', message: t('Printing Prescription')}));
                props.onSave(data);
                setSelected(null);
            }
        }
    };

    useEffect(() => {
        if((props.type === "paedSafeguarding" && workflowData.type === WORKFLOW_OPTIONS.SAFEGUARDING) ||
            (props.type === "rockwood" && workflowData.type ===WORKFLOW_OPTIONS.CLINICAL_FRAILTY_SCORE))
            newEntry();
    }, [workflowData]);

    const newEntry = () => {
        setSelected(null);
        setOpenDialog(true);
    };

    const getInjuryDate = () => {
        if (selected) {
            const dateUTC = UtilsMomentJS.getLocalDateFromUTCECDSFormat(selected?.injuryDate, selected?.injuryTime);
            return dateUTC.format(config.defaultDateTimeFormat);
        }
        return '-'
    }

    const showValue = () => {
        // eslint-disable-next-line default-case
        switch (props.type) {
            // 4AT Screening for Delirium and Cognitive Impairment
            case "delirium": {
                return (
                    <ul>
                        {selected?.values?.cat1 &&
                            <li>{`[ ${selected?.values?.cat1?.score} ]`} {selected?.values?.cat1?.text}</li>}
                        {selected?.values?.cat2 &&
                            <li>{`[ ${selected?.values?.cat2?.score} ]`} {selected?.values?.cat2?.text}</li>}
                        {selected?.values?.cat3 &&
                            <li>{`[ ${selected?.values?.cat3?.score} ]`} {selected?.values?.cat3?.text}</li>}
                        {selected?.values?.cat4 &&
                            <li>{`[ ${selected?.values?.cat4?.score} ]`} {selected?.values?.cat4?.text}</li>}
                    </ul>
                );
            }

            // Clinical Frailty Score
            case "rockwood": {
                return (
                    <Fragment>
                        <p>{selected?.scale?.description ? selected?.scale?.description : selected?.scale?.__description}</p>
                        {selected?.details !== "" &&
                            <p>{selected?.details}</p>
                        }
                        {(selected?.scale?.__code == 4 || selected?.scale?.__code == 5) &&
                            <ul>
                                <li>{`Patient consent to referral: ${selected?.patientConsentToReferral ? "Yes" : "No"}`}</li>
                                <li>{`Referral has been made: ${selected?.referralCompleted ? "Yes\n" : "No"}`}</li>
                            </ul>
                        }
                    </Fragment>
                );
            }

            // Injury Data Set
            case "injury": {
                return (
                    <Fragment>
                        {selected?.ageAtComplete < 18 &&
                            <Fragment>
                                {t("Escort: ")} {_.isEmpty(selected?.cwilted?.escort) && "-"}

                                <ul>
                                    {!_.isEmpty(selected?.cwilted?.escort) && selected?.cwilted?.escort.map(escort => (
                                        <li key={escort.id}>
                                            {`${escort?.name}`}
                                            {escort?.age ? `, Age: ${escort?.age}` : ""}
                                            {escort?.relationship ? `, Relationship: ${escort?.relationship}` : ""}
                                            {`, Attended with Child: ${escort?.attendedWithChild ? "Yes" : "No"}`}
                                            {`, Parental Responsibility: ${escort?.parentalResponsibility ? "Yes" : "No"}`}
                                            {escort?.contact ? `, Contact: ${escort?.contact}` : ""}
                                        </li>
                                    ))
                                    }
                                </ul>

                                <Divider />

                                <ul>
                                    {selected?.cwilted?.incident &&
                                        <li>{t("Concern and Incident: ")} {selected?.cwilted?.incident}</li>}
                                    {selected?.cwilted?.witnessed &&
                                        <li>{t("Witnessed: ")} {selected?.cwilted?.witnessed ? "Yes" : "No"}</li>}
                                    {selected?.cwilted?.details &&
                                        <li>{t("Witnessed Details: ")} {selected?.cwilted?.details}</li>}
                                    {selected?.cwilted?.condition &&
                                        <li>{t("Condition: ")} {selected?.cwilted?.condition}</li>}
                                    {selected?.cwilted?.demeanour &&
                                        <li>{t("Demeanour: ")} {selected?.cwilted?.demeanour}</li>}
                                    {selected?.cwilted?.incidentDate && selected?.cwilted?.incidentTime &&
                                        <li>{t("Illness Date Time: ")} {selected?.cwilted?.incidentDate} {moment(selected?.cwilted?.incidentTime).isValid()
                                            ? moment(selected?.cwilted?.incidentTime).format(config.defaultTimeFormat) : selected?.cwilted?.incidentTime}</li>}
                                    {selected?.cwilted?.incidentLocation &&
                                        <li>{t("Illness Location: ")} {selected?.cwilted?.incidentLocation?.__description}</li>
                                    }
                                    {selected?.cwilted?.incidentLocationName &&
                                        <li>{t("Location Name: ")} {selected?.cwilted?.incidentLocationName}</li>
                                    }
                                </ul>
                            </Fragment>
                        }

                        <ul>
                            {selected?.injuryDate && selected?.injuryTime &&
                                <li>{t("Injury Date Time: ")} {getInjuryDate()}</li>}
                            {selected?.placeOfInjury &&
                                <li>{t("Place of Injury: ")} {selected?.placeOfInjury?.__description}</li>}
                            {selected?.injuryIntent &&
                                <li>{t("Injury Intent: ")} {selected?.injuryIntent?.__description}</li>}
                            {selected?.injuryActivityStatus &&
                                <li>{t("Injury Activity Status: ")} {selected?.injuryActivityStatus?.__description}</li>}
                            {selected?.injuryActivityType &&
                                <li>{t("Injury Activity Type: ")} {selected?.injuryActivityType?.__description}</li>}
                            {selected?.injuryMechanism &&
                                <li>{t("Injury Mechanism: ")} {selected?.injuryMechanism?.__description}</li>}
                            {!_.isEmpty(selected?.injuryAlcoholOrDrug) &&
                                <li>{t("Injury Alcohol or Drug: ")} {selected?.injuryAlcoholOrDrug.map(entry => {
                                    return entry?.__description + ", ";
                                })}</li>}
                            {selected?.assaultLocationDescription &&
                                <li>{t('Assault Location Description: ')} {selected?.assaultLocationDescription}</li>}
                        </ul>
                    </Fragment>
                );
            }

            // Patient Safety Checklist
            case "paedSafeguarding": {
                return (
                    <Fragment>
                        <ul>
                            {selected?.safeguarding?.level &&
                                <h2>{t(`Safeguarding Level: ${_.startCase(selected?.safeguarding?.level)}`)}</h2>
                            }

                            {selected?.paediatric?.socialCare &&
                                <li>
                                    <p>{t(`Do you have any help from social care or any other agency? ${selected?.paediatric?.socialCare}`)}</p>
                                    <p>{t(`Additional Info: ${selected?.paediatric?.socialCareInfo}`)}</p>
                                </li>
                            }
                            {selected?.paediatric?.socialCare === "Yes" &&
                                <li>{t(`Social Worker: ${selected?.paediatric?.socialCareName} - ${selected?.paediatric?.socialCareContact}`)}</li>
                            }
                            {selected?.paediatric?.lookedAfterChild &&
                                <li>
                                    <p>{t(`Is this a looked after child? ${selected?.paediatric?.lookedAfterChild}`)}</p>
                                    <p>{t(`Additional Info: ${selected?.paediatric?.lookedAfterChildInfo}`)}</p>
                                </li>
                            }
                            {selected?.paediatric?.agreement &&
                                <li>
                                    <p>{t(`Do you consent to share information? ${selected?.paediatric?.agreement}`)}</p>
                                    <p>{t(`Additional Info: ${selected?.paediatric?.agreementInfo}`)}</p>
                                </li>
                            }
                            {selected?.paediatric?.CPOCPIS &&
                                <li>
                                    <p>{t(`Check any CPO and CPIS Information:`)}</p>
                                    {t(selected?.paediatric?.CPOCPIS)}
                                </li>
                            }
                            {selected?.paediatric?.attendances &&
                                <li>
                                    <p>{t(`Review number of attendances in last 12 months - ED or other UEC:`)}</p>
                                    {t(selected?.paediatric?.attendances)}
                                </li>
                            }
                            {selected?.safeguarding?.reasons?.reason &&
                                <li>
                                    <p>{t(`Safeguarding Reason:`)}</p>
                                    {t(selected?.safeguarding?.reasons?.reason)}
                                </li>
                            }
                            {!_.isEmpty(selected?.safeguarding?.reasons.safeguarding) &&
                                <Fragment>
                                    <h3>{t("Safeguarding SNOMED")}</h3>
                                    {selected?.safeguarding?.reasons.safeguarding.map(el => (
                                        <li key={el.snomedCode}>
                                            [{el.snomedCode}] {el.__description}
                                        </li>
                                    ))}
                                </Fragment>
                            }
                            <li>{t("Action Taken")}</li>
                            <p>
                                {t(`Informed Nurse in Charge: ${selected?.safeguarding?.reasons.infoNurse ? "Yes" : "No"}`)}
                                <br/>
                                {t(`Discussed with: Trust Safeguarding Team: ${selected?.safeguarding?.reasons.trustSafeguarding ? "Yes" : "No"}`)}
                                <br/>
                                {t(`Referral to: Health Visitor/Early Help/School Nurse: ${selected?.safeguarding?.reasons.refHealthVisitor ? "Yes" : "No"}`)}
                                <br/>
                                {t(`Referral to: CHaD: ${selected?.safeguarding?.reasons.refCHaD ? "Yes" : "No"}`)}
                                <br/>
                                {t(`Referral to: CAMHS: ${selected?.safeguarding?.reasons.refCAMHS ? "Yes" : "No"}`)}
                                <br/>
                                {t(`Referral to: Other: ${selected?.safeguarding?.reasons.refOther ? "Yes" : "No"}`)}
                                <br/>
                                {t(`Referral to: Police: ${selected?.safeguarding?.reasons.refPolice ? "Yes" : "No"}`)}
                                <br/>
                                {t(`Additional Referral Information: ${selected?.safeguarding?.reasons.additionalInfo ? "Yes" : "No"}`)}
                            </p>
                            {selected?.safeguarding?.reasons?.refOther &&
                                <li>
                                    {t(`Referral to Other: ${selected?.safeguarding?.reasons.refOtherDescription}`)}
                                </li>
                            }
                            {selected?.safeguarding?.reasons?.additionalInfo &&
                                <li>
                                    {t(`Additional Referral Information: ${selected?.safeguarding?.reasons.additionalInfoDescription}`)}
                                </li>
                            }
                        </ul>
                    </Fragment>
                );
            }

            case 'prescription': {
                return (
                    <>
                        <h3>{t('Drugs')}:</h3>
                        <div>{t("Prescription printed")}: {selected?.values?.printCount}</div>
                        <table style={{width: '100%'}}>
                            <thead>
                                <tr>
                                    <th>{t('Drug')}</th>
                                    <th>{t('Dose')}</th>
                                    <th>{t('Route')}</th>
                                    <th>{t('Frequency')}</th>
                                    <th>{t('Duration')}</th>
                                </tr>
                            </thead>

                            {selected?.values?.drugs.map(drug => (
                                <tbody key={drug.id}>
                                    <tr>
                                        <td>{drug.drug}</td>
                                        <td>{drug.dose}</td>
                                        <td>{drug.route}</td>
                                        <td>{drug.frequency}</td>
                                        <td>{drug.duration}</td>
                                    </tr>
                                </tbody>
                            ))}
                        </table>
                    </>
                );
            }

            // Emed3
            case 'fitnessForWork': {
                return (
                    <Fragment>
                        <p> {`I assessed your case on:`} <b>{`${selected?.assessedCase ? moment(selected?.assessedCase).format(config.defaultDateFormat) : '-'}`} </b></p>
                        <p> {`and because of the following condition(s):`} <b>{`${selected?.assessedCaseDetails ? selected?.assessedCaseDetails : '-'}`}</b></p>
                        <p> {`I advise you that:`} <b>{`${selected?.advise === 'maybeFit' ? 'you may be fit for work' : selected?.advise === 'notFit' ? 'you are not fit for work' : '-'}`}</b></p>
                        <p> {`This will be the case for:`} <b>{`${selected?.caseForNumber ? selected?.caseForNumber : '-'} ${selected?.caseForInterval ? selected?.caseForInterval : ''}(s)`}</b></p>
                        <p> {`or from`} <b>{`${selected?.caseFrom ? moment(selected?.caseFrom).format(config.defaultDateFormat) : '-'}`}</b> {`to`} <b>{`${selected?.caseTo ? moment(selected?.caseTo).format(config.defaultDateFormat) : '-'}`}</b></p>
                        <p> {`NI Number:`} <b>{`${selected?.niNumber ? selected?.niNumber : '-'}`}</b></p>
                        <p> {`Unique Id:`} <b>{`${selected?.uniqueId ? selected?.uniqueId : '-'}`}</b></p>
                    </Fragment>
                );
            }
        }
    };

    const checkIfItsFormParentForm = (element) => {
        if (element?.parentForm) {
            return <div>{`#${element.parentForm}`}</div>;
        } else {
            // For old data structure
            if (element?.fromTriage) {
                return <div>#Triage</div>;
            }
            if (element?.fromClerking) {
                return <div>#Clerking</div>;
            }
        }
    };

    const handleCloseDialog = (event, reason) => {
        if (reason !== 'backdropClick') {
            setOpenDialog(false);

            if(extended){
                setExtended(false);
            }

            if(loadDialog){
                setLoadDialog(false);
            }

            if (props.type === 'rockwood') {
                if(workflowData.type === WORKFLOW_OPTIONS.CLINICAL_FRAILTY_SCORE)
                    dispatch(nextWorkflowStep());
            }

            if (props.type === 'paedSafeguarding') {
                if(workflowData.type === WORKFLOW_OPTIONS.SAFEGUARDING)
                    dispatch(nextWorkflowStep());
            }
        }
    };

    const openExtendDialog = () => {
        setOpenDialog(true)
        setExtended(true)
    };
    const openLoadDialog = () => {
        setOpenDialog(true)
        setLoadDialog(true)
    };

    useEffect(() => {
        if (props.agyleDialogInsertButtons) {
            const buttons = [];

            if (!props.disableSave) {
                if (selected) {
                    buttons.push(
                        <>
                            <Tooltip title={props.patientHaveModification ? "Save changes to cancel" : ""}
                                     placement="top">
                                <div>
                                    <AgyleButton variant={"contained"}
                                                 disabled={selected?.canceled || props.patientHaveModification}
                                                 onClick={openConfirmDelete}> {t("Cancel selected")}
                                    </AgyleButton>
                                </div>
                            </Tooltip>
                            {(props.type !== 'fitnessForWork') &&
                                <AgyleButton style={{marginLeft: 10}}
                                             onClick={() => setOpenDialog(true)}
                                             variant={"contained"}>
                                    {t("Load selected")}
                                </AgyleButton>
                            }
                            {(selected && props.type === 'fitnessForWork') &&
                                <>
                                    <AgyleButton style={{marginLeft: 10}}
                                                 disabled={selected?.canceled}
                                                 onClick={openExtendDialog}
                                                 variant={"contained"}>
                                        {t("Extend")}
                                    </AgyleButton>
                                    <AgyleButton style={{marginLeft: 10}}
                                                 onClick={openLoadDialog}
                                                 variant={"contained"}>
                                        {t("Load selected")}
                                    </AgyleButton>
                                </>

                            }
                        </>
                    )
                }

                buttons.push(
                  <AgyleButton variant={"contained"}
                               onClick={newEntry}>
                      {t("Create new")}
                  </AgyleButton>
                )
            }

            props.agyleDialogInsertButtons(buttons);
        }
    }, [props.patientHaveModification, selected, props.disableSave, props.agyleDialogInsertButtons, dataSet, openDialog, isConfirmDeleteOpen, workflowData]);


    return (
        <>
            {!props.agyleDialogInsertButtons && <DialogTitle className={clsx(classesLocal.background, classesLocal.dialogTitle)}>
                {t(props.title)}
            </DialogTitle>}

            <DialogContent className={clsx("scroll-normal-white", classesLocal.background)} dividers>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                        <Paper
                            style={{padding: '10px 10px'}}
                            className={clsx(getTableScrollbar(), classesLocal.historyPanel)}
                        >
                            {!_.isEmpty(dataSet) && dataSet.map(element => (
                                <Button
                                    key={element.id}
                                    onClick={() => setSelected(element)}
                                    className={`${selected === element ? classesLocal.historyButtonActive : classesLocal.historyButton}`}
                                >
                                    <Grid item xs={3} style={{paddingTop: 8}}>
                                        <AssignmentIcon fontSize={'large'}/>
                                    </Grid>

                                    <Grid
                                        item xs={9}
                                        style={{fontSize: '10px'}}
                                        className={clsx(element?.canceled && classesLocal.canceled)}
                                    >
                                        {checkIfItsFormParentForm(element)}

                                        {moment(element.date, config.defaultDateTimeFormat).isValid()
                                            ? element.date : moment(element.date).format(config.defaultDateTimeFormat)}
                                        <br/>

                                        {t(`Created by: ${element.by}`)}<br/>

                                        {props.showScore &&
                                            t(`Score: ${_.get(element, props.scoreFiled)}`)
                                        }
                                    </Grid>
                                </Button>
                            ))}
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={7}>
                        <Paper
                            style={{padding: '10px 10px'}}
                            className={clsx(getTableScrollbar(), classesLocal.detailsPanel)}
                        >
                            {selected &&
                                <div className={clsx(selected?.canceled && classesLocal.canceled)}>
                                    <h2 style={{textAlign: 'center'}}>
                                        {t('Quick Summary')}
                                    </h2>

                                    {selected.fromTriage &&
                                        <p className={classesLocal.triage}>
                                            {t('The entry was created in triage')}
                                        </p>
                                    }

                                    {selected.fromClerking &&
                                        <p className={classesLocal.triage}>
                                            {t('The entry was created in clerking')}
                                        </p>
                                    }

                                    <span>{t(`Entry created by ${selected?.by} at ${moment(selected.date, config.defaultDateTimeFormat).isValid()
                                        ? selected.date : moment(selected.date).format(config.defaultDateTimeFormat)}`)}</span>

                                    {props.showScore &&
                                        <>
                                            <p>
                                                {t(`Total score: ${_.get(selected, props.scoreFiled)}`)}
                                            </p>

                                            {selected.lastScore &&
                                                <span>
                                                    {t('Trend compared to the last entry: ')} {Number(selected.lastScore) > Number(_.get(selected, props.scoreFiled)) ? '↓' :
                                                    Number(selected.lastScore) === Number(_.get(selected, props.scoreFiled)) ? '-' : '↑'}
                                                </span>
                                            }
                                        </>
                                    }

                                    <p>
                                        {t(`Selected options`)}
                                    </p>

                                    {showValue()}
                                </div>
                            }
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            {!props.agyleDialogInsertButtons && <DialogActions>
                {!(props.triage || props.clerking) &&
                    <div style={{flex: '1 0 0'}}>
                        <AgyleButton onClick={props.onClose}>
                            {t("Close")}
                        </AgyleButton>
                    </div>
                }

                {(selected && props.type === 'prescription' && dispatch(hasPermission(Permissions.AGYLE_CAN_ADD_PRESCRIPTION))) &&
                    <AgyleButton
                        variant="contained"
                        disabled={selected?.canceled}
                        onClick={openConfirmRePrint}
                    >
                        {t('Print Prescription')}
                    </AgyleButton>
                }

                {!props.disableSave &&
                    <>
                        {selected &&
                            <>
                                <AgyleButton
                                    variant="contained"
                                    disabled={selected?.canceled || props.patientHaveModification}
                                    onClick={openConfirmDelete}
                                >
                                    {t('Cancel selected')}
                                </AgyleButton>

                                <AgyleButton
                                    style={{marginLeft: 10}}
                                    onClick={() => setOpenDialog(true)}
                                    variant="contained"
                                >
                                    {t('Load selected')}
                                </AgyleButton>
                            </>
                        }

                        <AgyleButton
                            variant="contained"
                            onClick={newEntry}
                        >
                            {t('Create new')}
                        </AgyleButton>
                    </>
                }
            </DialogActions>}

            <AgyleDialog
                title={props.title}
                open={openDialog}
                width={props.width ? props.width : "md"}
                fullWidth
                onClose={handleCloseDialog}
                disableSave
            >
                {props.component &&
                    React.cloneElement(props.component, {
                    onSave: saveData,
                    onClose: () => setOpenDialog(false),
                    selected: selected,
                    parentForm: props.parentForm,
                    histPresComplaint: props?.component?.props?.histPresComplaint,
                    extended: extended,
                    loadDialog: loadDialog,

                })}
            </AgyleDialog>

            <MessageBox
                onClose={closeConfirmDelete}
                title={t("Confirmation")}
                message={t("Are you sure you want to cancel this entry?")}
                haveAccept={{yes: t("Submit"), no: t("Close")}}
                onAccept={deleteEntry}
                open={isConfirmDeleteOpen}
                warning
            />

            <MessageBox
                open={isConfirmRePrintOpen}
                onClose={closeConfirmRePrint}
                title={t("Confirmation")}
                message={t("Are you sure you want to print this prescription? This Action will be recorded.")}
                haveAccept={{yes: t("Yes"), no: t("No")}}
                onAccept={rePrint}
            />
        </>
    );
};

export default MultipleEntriesFormList;